import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ElementType,
  type ReactNode,
} from "react"
import { useTranslation } from "react-i18next"
import { useNavigate, useSearchParams } from "react-router"
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  CartesianGrid,
  LineChart,
  Line,
  Legend,
} from "recharts"

import {
  Button,
  Badge,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Skeleton,
} from "@f2e/ui/components/base"

import { ErrorPage } from "@/components/System"
import { PermissionButton } from "@/components/Auth"
import {
  AlertCircle,
  ArrowRight,
  BarChart3,
  CheckCircle2,
  Circle,
  ClipboardCheck,
  Copy,
  CreditCard,
  ExternalLink,
  MessageSquare,
  Rocket,
  Send,
  UserRoundCheck,
} from "lucide-react"
import { useStore } from "@/store"
import { useToast } from "@/hooks"
import { SETTINGS_ENDPOINTS } from "@/constants"
import {
  CreditUsageRecordsModal,
  CreditUsageTrendChartCard,
} from "@/components/Billing"
import {
  FetchStatus,
  useGetOrganizationCreditUsageApi,
  useGetClientChannelReadinessSummaryApi,
  useGetHumanHandoffSummaryApi,
  useGetHistoriesApi,
  useListClientUsersApi,
  useGetWorkspaceDashboardMetricsApi,
  historyResponseToHistory,
  transformCreditUsage,
  transformWorkspaceDashboardData,
  type ClientChannelReadinessSummary,
  type HumanHandoffSummary,
  type HumanHandoffSummaryItem,
} from "@/hooks/api"
import { useGetEvaluationCaseCountApi } from "@/hooks/api/eval"
import {
  WorkspaceDashboardData,
  DashboardChartPoint,
  WorkspaceDashboardCards,
  DashboardSeries,
} from "@/types/metrics"
import {
  OrganizationRole,
  Permission,
  type History,
  type Workspace,
} from "@/types"
import { getUserFacingApiErrorMessage } from "@/lib/apiErrors"

// Allowed day ranges for dashboard queries (business default: 7/14/30)
const RANGE_OPTIONS = [7, 14, 30] as const
type RangeOption = (typeof RANGE_OPTIONS)[number]

type ReadinessStepState = "complete" | "active" | "pending"

interface LaunchReadinessStep {
  id: string
  title: string
  description: string
  state: ReadinessStepState
  // Live URL surfaced inside the step so users can see and open what they share
  linkUrl?: string
}

interface LaunchReadinessAction {
  label: string
  onClick: () => void
  // Outline when the brief already shows the same action as the solid primary
  variant?: "outline"
  requiresWorkspaceEdit?: boolean
}

export type LaunchReadinessLifecycleState =
  | "noAgent"
  | "agentUnpublished"
  | "noChannel"
  | "channelUnavailable"
  | "channelUnhealthy"
  | "ownershipAmbiguous"
  | "ownershipMissing"
  | "ready"

export const resolveLaunchReadinessLifecycleState = (
  summary: ClientChannelReadinessSummary
): LaunchReadinessLifecycleState => {
  if (summary.agent_count === 0) return "noAgent"
  if (summary.published_agent_count === 0) return "agentUnpublished"
  if (summary.published_channel_count === 0) return "noChannel"
  if (summary.confirmed_ready_channel_count > 0) return "ready"
  if (summary.external_health_coverage_status === "incomplete")
    return "channelUnavailable"
  if (summary.health_status === "unavailable") return "channelUnavailable"
  if (summary.health_status === "unhealthy") return "channelUnhealthy"
  if (summary.ownership_status === "ambiguous") return "ownershipAmbiguous"
  return "ownershipMissing"
}

const formatNumber = (value: number, precision = 0) => {
  return new Intl.NumberFormat(undefined, {
    minimumFractionDigits: precision,
    maximumFractionDigits: precision,
  }).format(value)
}

const formatCredits = (value: number) => {
  return new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value)
}

const buildHandoffItemUrl = (
  workspaceId: string,
  item: HumanHandoffSummaryItem
) => {
  if (item.history_id) {
    const params = new URLSearchParams({
      tab: "conversations",
      wid: workspaceId,
      hid: item.history_id.toString(),
      focus: "history",
    })
    if (item.page_no) params.set("page", item.page_no.toString())
    return `/workspaces/${workspaceId}?${params.toString()}`
  }
  return `/workspaces/${workspaceId}/users/${item.client_user_id}`
}

const getHandoffTargetUrl = (
  workspaceId: string,
  item?: HumanHandoffSummaryItem
) => {
  if (item) {
    return buildHandoffItemUrl(workspaceId, item)
  }
  return `/workspaces/${workspaceId}?tab=user-management`
}

const getHandoffDisplayName = (item: HumanHandoffSummaryItem) =>
  item.client_user_display_name || item.external_user_id || item.client_user_id

const HANDOFF_SOURCE_LABEL_KEYS: Record<string, string> = {
  agent_handoff: "dashboard.handoffs.sources.agentHandoff",
  agent_policy: "dashboard.handoffs.sources.agentPolicy",
  crm_manual: "dashboard.handoffs.sources.crmManual",
}

const HANDOFF_CHANNEL_LABEL_KEYS: Record<string, string> = {
  web: "historyList.channelTypeSelect.options.web",
  widget: "historyList.channelTypeSelect.options.widget",
  browser_extension: "historyList.channelTypeSelect.options.browserExtension",
  slack: "historyList.channelTypeSelect.options.slack",
  line: "historyList.channelTypeSelect.options.line",
  messenger: "historyList.channelTypeSelect.options.messenger",
  instagram: "historyList.channelTypeSelect.options.instagram",
}

const formatDashboardLabel = (
  date: string,
  rangeDays: number,
  locale?: string
) => {
  const parsedDate = new Date(`${date}T00:00:00Z`)
  const formatter =
    rangeDays > 7
      ? new Intl.DateTimeFormat(locale, {
          month: "short",
          day: "numeric",
          timeZone: "UTC",
        })
      : new Intl.DateTimeFormat(locale, {
          weekday: "short",
          timeZone: "UTC",
        })

  return formatter.format(parsedDate)
}

const formatActivityTime = (date: string, locale?: string) => {
  if (!date) return "—"

  const parsedDate = new Date(date)
  if (Number.isNaN(parsedDate.getTime())) return "—"

  return new Intl.DateTimeFormat(locale, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(parsedDate)
}

const buildChartData = (
  points: DashboardChartPoint[],
  rangeDays: number,
  locale?: string
): { label: string; value: number; date: string }[] => {
  return points.map((point) => {
    return {
      label: formatDashboardLabel(point.date, rangeDays, locale),
      value: point.value,
      date: point.date,
    }
  })
}

const buildSeriesChartData = (
  series: DashboardSeries[],
  rangeDays: number,
  locale?: string
): {
  data: Array<Record<string, number | string>>
  seriesMeta: Array<{ id: string; name: string }>
} => {
  if (!series.length) {
    return { data: [], seriesMeta: [] }
  }

  const dateSet = new Set<string>()
  const seriesMaps = series.map((item) => {
    const map = new Map<string, number>()
    item.points.forEach((point) => {
      map.set(point.date, point.value)
      dateSet.add(point.date)
    })
    return map
  })

  const sortedDates = Array.from(dateSet).sort()
  const data = sortedDates.map((date) => {
    const row: Record<string, number | string> = {
      label: formatDashboardLabel(date, rangeDays, locale),
      date,
    }
    series.forEach((item, index) => {
      row[item.id] = seriesMaps[index].get(date) ?? 0
    })
    return row
  })

  return {
    data,
    seriesMeta: series.map((item) => ({ id: item.id, name: item.name })),
  }
}

// The chart range ends on the in-progress day; splitting it into a dashed
// "partial" series keeps live numbers visible without reading as a drop-off.
const splitPartialPoint = <T extends { date: string; value: number }>(
  data: T[],
  partialDate?: string
) => {
  const lastIndex = data.length - 1
  const hasPartialPoint =
    Boolean(partialDate) &&
    lastIndex >= 0 &&
    data[lastIndex].date === partialDate
  const chartData = data.map((point, index) => ({
    ...point,
    solidValue: hasPartialPoint && index === lastIndex ? null : point.value,
    partialValue:
      hasPartialPoint && index >= lastIndex - 1 ? point.value : null,
  }))
  return { chartData, hasPartialPoint }
}

export const UsageChartTooltip = ({
  active,
  payload,
  series,
  formatValue,
  partialDate,
  partialLabel,
}: {
  active?: boolean
  payload?: Array<{ payload?: Record<string, unknown> }>
  series: Array<{ key: string; label: string; color?: string }>
  formatValue: (value: number) => string
  partialDate?: string
  partialLabel: string
}) => {
  const row = payload?.[0]?.payload as
    | ({ date?: string } & Record<string, unknown>)
    | undefined
  if (!active || !row?.date) return null

  const showSwatch = series.length > 1

  return (
    <div className="rounded-lg border border-border bg-popover px-3 py-2 text-sm text-popover-foreground shadow-lg">
      <div className="font-medium">{row.date}</div>
      <div className="mt-1 space-y-0.5">
        {series.map((item) => {
          const value = row[item.key]
          if (typeof value !== "number") return null
          return (
            <div
              key={item.key}
              className="flex items-center justify-between gap-4"
            >
              <span className="flex items-center gap-1.5 text-muted-foreground">
                {showSwatch ? (
                  <span
                    aria-hidden
                    className="h-2 w-2 shrink-0 rounded-full"
                    style={{ backgroundColor: item.color }}
                  />
                ) : null}
                {item.label}
              </span>
              <span className="font-medium tabular-nums">
                {formatValue(value)}
              </span>
            </div>
          )
        })}
      </div>
      {row.date === partialDate ? (
        <div className="mt-1 text-xs text-muted-foreground">{partialLabel}</div>
      ) : null}
    </div>
  )
}

const ChartCard = ({
  title,
  subtitle,
  details,
  headerAction,
  data,
  color,
  isLoading,
  emptyLabel,
  seriesLabel,
  partialDate,
  formatValue = (value: number) => formatNumber(value),
  yAxisWidth = 28,
  chartHeight = 260,
}: {
  title: string
  subtitle?: string
  details?: string
  headerAction?: ReactNode
  data: { label: string; value: number; date: string }[]
  color: string
  isLoading: boolean
  emptyLabel: string
  seriesLabel: string
  partialDate?: string
  formatValue?: (value: number) => string
  yAxisWidth?: number
  chartHeight?: number
}) => {
  const { t } = useTranslation()
  const fillId = `fill-${color.replace("#", "")}`
  const axisColor = "hsl(var(--muted-foreground))"
  const { chartData, hasPartialPoint } = splitPartialPoint(data, partialDate)

  return (
    <Card>
      <CardHeader className="gap-3">
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-1">
            <CardTitle className="text-base font-semibold text-slate-800 dark:text-slate-200">
              {title}
            </CardTitle>
            {subtitle ? (
              <p className="text-sm text-slate-500 dark:text-slate-400">
                {subtitle}
              </p>
            ) : null}
            {details ? (
              <p className="text-sm text-slate-500 dark:text-slate-400">
                {details}
              </p>
            ) : null}
          </div>
          {headerAction ? <div className="shrink-0">{headerAction}</div> : null}
        </div>
      </CardHeader>
      <CardContent className="px-4 pb-6" style={{ height: chartHeight }}>
        {isLoading ? (
          <Skeleton className="h-full w-full" />
        ) : data.length === 0 ? (
          <div className="flex h-full items-center justify-center text-sm text-slate-500">
            {emptyLabel}
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ left: 0, right: 6, top: 10 }}>
              <defs>
                <linearGradient id={fillId} x1="0" x2="0" y1="0" y2="1">
                  <stop offset="0%" stopColor={color} stopOpacity={0.25} />
                  <stop offset="100%" stopColor={color} stopOpacity={0.05} />
                </linearGradient>
              </defs>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="hsl(var(--border))"
              />
              <XAxis
                dataKey="label"
                tickLine={false}
                axisLine={false}
                tick={{ fill: axisColor, fontSize: 12 }}
              />
              <YAxis
                tickLine={false}
                axisLine={false}
                allowDecimals={false}
                tick={{ fill: axisColor, fontSize: 12 }}
                width={yAxisWidth}
                tickFormatter={formatValue}
              />
              <RechartsTooltip
                content={
                  <UsageChartTooltip
                    series={[{ key: "value", label: seriesLabel }]}
                    formatValue={formatValue}
                    partialDate={hasPartialPoint ? partialDate : undefined}
                    partialLabel={t("dashboard.charts.partialToday")}
                  />
                }
              />
              <Area
                type="monotone"
                dataKey="solidValue"
                stroke={color}
                strokeWidth={2}
                fillOpacity={1}
                fill={`url(#${fillId})`}
                dot={{ r: 3, strokeWidth: 1, stroke: color }}
                activeDot={{ r: 5, strokeWidth: 2 }}
              />
              {hasPartialPoint ? (
                <Area
                  type="monotone"
                  dataKey="partialValue"
                  stroke={color}
                  strokeWidth={2}
                  strokeDasharray="4 4"
                  fill="transparent"
                  dot={{ r: 3, strokeWidth: 1, stroke: color }}
                  activeDot={{ r: 5, strokeWidth: 2 }}
                />
              ) : null}
            </AreaChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  )
}

const MultiLineChartCard = ({
  title,
  data,
  series,
  isLoading,
  emptyLabel,
  partialDate,
}: {
  title: string
  data: Array<Record<string, number | string>>
  series: Array<{ id: string; name: string }>
  isLoading: boolean
  emptyLabel: string
  partialDate?: string
}) => {
  const { t } = useTranslation()
  const axisColor = "hsl(var(--muted-foreground))"
  const colors = [
    "#2563EB",
    "#10B981",
    "#F97316",
    "#8B5CF6",
    "#EC4899",
    "#14B8A6",
    "#FACC15",
  ]
  const lastIndex = data.length - 1
  const hasPartialPoint =
    Boolean(partialDate) &&
    lastIndex >= 0 &&
    data[lastIndex].date === partialDate
  // Original per-series keys stay intact for the tooltip; derived keys split
  // each line into a solid run and a dashed final segment for today.
  const chartData = data.map((row, index) => {
    const next: Record<string, number | string | null> = { ...row }
    series.forEach((item) => {
      const value =
        typeof row[item.id] === "number" ? (row[item.id] as number) : 0
      next[`${item.id}__solid`] =
        hasPartialPoint && index === lastIndex ? null : value
      next[`${item.id}__partial`] =
        hasPartialPoint && index >= lastIndex - 1 ? value : null
    })
    return next
  })

  return (
    <Card className="h-full">
      <CardHeader>
        <CardTitle className="text-base font-semibold text-slate-800 dark:text-slate-200">
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="h-[280px] px-4 pb-6">
        {isLoading ? (
          <Skeleton className="h-full w-full" />
        ) : data.length === 0 ? (
          <div className="flex h-full items-center justify-center text-sm text-slate-500">
            {emptyLabel}
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ left: 0, right: 8, top: 10 }}>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="hsl(var(--border))"
              />
              <XAxis
                dataKey="label"
                tickLine={false}
                axisLine={false}
                tick={{ fill: axisColor, fontSize: 12 }}
              />
              <YAxis
                tickLine={false}
                axisLine={false}
                allowDecimals={false}
                tick={{ fill: axisColor, fontSize: 12 }}
                width={28}
              />
              <RechartsTooltip
                content={
                  <UsageChartTooltip
                    series={series.map((item, index) => ({
                      key: item.id,
                      label: item.name,
                      color: colors[index % colors.length],
                    }))}
                    formatValue={(value: number) => formatNumber(value)}
                    partialDate={hasPartialPoint ? partialDate : undefined}
                    partialLabel={t("dashboard.charts.partialToday")}
                  />
                }
              />
              <Legend />
              {series.map((item, index) => (
                <Line
                  key={item.id}
                  type="monotone"
                  dataKey={`${item.id}__solid`}
                  name={item.name}
                  stroke={colors[index % colors.length]}
                  strokeWidth={2}
                  dot={{ r: 2 }}
                  activeDot={{ r: 4 }}
                />
              ))}
              {hasPartialPoint
                ? series.map((item, index) => (
                    <Line
                      key={`${item.id}__partial`}
                      type="monotone"
                      dataKey={`${item.id}__partial`}
                      legendType="none"
                      stroke={colors[index % colors.length]}
                      strokeWidth={2}
                      strokeDasharray="4 4"
                      dot={{ r: 2 }}
                      activeDot={{ r: 4 }}
                    />
                  ))
                : null}
            </LineChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  )
}

const OpsSignal = ({
  icon: Icon,
  label,
  value,
  detail,
  onClick,
  tone = "neutral",
}: {
  icon: ElementType
  label: string
  value: string
  detail: string
  onClick?: () => void
  tone?: "good" | "attention" | "neutral"
}) => {
  const toneClass = {
    good: "text-emerald-700 bg-emerald-50 dark:text-emerald-300 dark:bg-emerald-950/30",
    attention:
      "text-amber-700 bg-amber-50 dark:text-amber-300 dark:bg-amber-950/30",
    neutral:
      "text-slate-700 bg-slate-50 dark:text-slate-300 dark:bg-slate-900/60",
  }[tone]
  const isInteractive = Boolean(onClick)
  const className = `group grid min-h-[116px] min-w-0 grid-rows-[2rem_2.5rem_auto] rounded-md border-t border-slate-200 px-3 pt-3 pb-2 text-left transition dark:border-slate-800 ${
    isInteractive
      ? "cursor-pointer hover:border-slate-300 hover:bg-slate-50/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 dark:hover:border-slate-700 dark:hover:bg-slate-900/60"
      : "cursor-default"
  }`
  const content = (
    <>
      <div className="flex min-h-8 items-start gap-2">
        <div
          className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md ${toneClass}`}
        >
          <Icon className="h-4 w-4" />
        </div>
        <div
          className={`line-clamp-2 min-w-0 text-xs font-medium leading-4 tracking-normal text-slate-500 transition dark:text-slate-400 ${
            isInteractive
              ? "group-hover:text-slate-700 dark:group-hover:text-slate-200"
              : ""
          }`}
        >
          {label}
        </div>
        {isInteractive ? (
          <ArrowRight className="ml-auto h-4 w-4 shrink-0 text-slate-400 opacity-0 transition group-hover:translate-x-0.5 group-hover:opacity-100 dark:text-slate-500" />
        ) : null}
      </div>
      <div
        className={`line-clamp-2 flex min-h-10 items-center text-base font-semibold leading-5 text-slate-950 transition dark:text-slate-50 ${
          isInteractive
            ? "group-hover:text-slate-700 dark:group-hover:text-slate-200"
            : ""
        }`}
      >
        {value}
      </div>
      <div className="line-clamp-2 text-sm leading-5 text-slate-600 dark:text-slate-400">
        {detail}
      </div>
    </>
  )

  if (!onClick) {
    return <div className={className}>{content}</div>
  }

  return (
    <button type="button" className={className} onClick={onClick}>
      {content}
    </button>
  )
}

const ActivityRow = ({
  label,
  value,
  detail,
  onClick,
}: {
  label: string
  value: string
  detail: string
  onClick: () => void
}) => (
  <button
    type="button"
    className="flex w-full items-center justify-between gap-4 border-b border-slate-200 py-3 text-left last:border-b-0 hover:bg-slate-50 md:border-b-0 dark:border-slate-800 dark:hover:bg-slate-900"
    onClick={onClick}
  >
    <span className="min-w-0">
      <span className="block text-sm font-medium text-slate-900 dark:text-slate-100">
        {label}
      </span>
      <span className="block text-sm text-slate-500 dark:text-slate-400">
        {detail}
      </span>
    </span>
    <span className="flex shrink-0 items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-300">
      {value}
      <ArrowRight className="h-4 w-4" />
    </span>
  </button>
)

interface HandoffPriorityPanelProps {
  errorMessage: string | null
  isLoading: boolean
  items: HumanHandoffSummaryItem[]
  needsReplyCount: number
  waitingOver30mCount: number
  onOpenItem: (item: HumanHandoffSummaryItem) => void
  onOpenPrimary: () => void
  workspace: Workspace
}

type DashboardRequestStatus = "loading" | "success" | "error"

const HandoffPriorityPanel = ({
  errorMessage,
  isLoading,
  items,
  needsReplyCount,
  waitingOver30mCount,
  onOpenItem,
  onOpenPrimary,
  workspace,
}: HandoffPriorityPanelProps) => {
  const { t } = useTranslation()

  if (isLoading) {
    return (
      <Card
        role="status"
        aria-label={t("dashboard.handoffs.label")}
        aria-busy="true"
        className="border-slate-200 bg-white shadow-xs dark:border-slate-800 dark:bg-slate-950"
      >
        <CardContent className="space-y-3 py-5">
          <Skeleton className="h-5 w-48" />
          <Skeleton className="h-4 w-72 max-w-full" />
        </CardContent>
      </Card>
    )
  }

  if (errorMessage) {
    return (
      <Card
        role="alert"
        className="border-amber-200 bg-amber-50 shadow-xs dark:border-amber-900/70 dark:bg-amber-950/30"
      >
        <CardHeader className="gap-1">
          <CardTitle className="text-base font-semibold text-amber-950 dark:text-amber-100">
            {t("dashboard.handoffs.unavailable")}
          </CardTitle>
          <p className="text-sm text-amber-900 dark:text-amber-200">
            {errorMessage}
          </p>
        </CardHeader>
      </Card>
    )
  }

  if (needsReplyCount <= 0) {
    return null
  }

  return (
    <Card className="border-amber-200 bg-amber-50 shadow-xs dark:border-amber-900/70 dark:bg-amber-950/30">
      <CardHeader className="gap-3 md:flex-row md:items-start md:justify-between">
        <div className="space-y-1">
          <CardTitle className="text-base font-semibold text-amber-950 dark:text-amber-100">
            {t("dashboard.handoffs.panelTitle")}
          </CardTitle>
          <p className="max-w-3xl text-sm text-amber-900 dark:text-amber-200">
            {t("dashboard.handoffs.panelDescription", {
              count: needsReplyCount,
            })}{" "}
            {t("dashboard.handoffs.waitingOver30Description", {
              count: waitingOver30mCount,
            })}
          </p>
        </div>
        <PermissionButton
          className="shrink-0 gap-2"
          permission={Permission.WsUpdateResources}
          workspace={workspace}
          disabledTooltip={t("common.noPermission")}
          onClick={onOpenPrimary}
        >
          {t("dashboard.handoffs.panelCta")}
          <ArrowRight className="h-4 w-4" aria-hidden="true" />
        </PermissionButton>
      </CardHeader>
      {items.length > 0 ? (
        <CardContent className="grid gap-3 pt-0 md:grid-cols-3">
          {items.map((item) => {
            const sourceKey = HANDOFF_SOURCE_LABEL_KEYS[item.source]
            const channelKey = item.client_channel_type
              ? HANDOFF_CHANNEL_LABEL_KEYS[item.client_channel_type]
              : undefined

            return (
              <button
                key={item.interaction_id}
                type="button"
                className="rounded-lg border border-amber-200 bg-card p-3 text-left text-sm transition-colors hover:bg-amber-100/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 dark:border-amber-900/70 dark:hover:bg-amber-900/40"
                onClick={() => onOpenItem(item)}
              >
                <div className="line-clamp-1 font-semibold text-foreground">
                  {getHandoffDisplayName(item)}
                </div>
                <div className="mt-1 text-muted-foreground">
                  {t("dashboard.handoffs.itemDetail", {
                    minutes: item.waiting_minutes,
                    source: t(
                      sourceKey ?? "dashboard.handoffs.sources.unknown"
                    ),
                    channel: t(
                      channelKey ?? "dashboard.handoffs.channels.unknown"
                    ),
                  })}
                </div>
                <div className="mt-2 text-xs font-medium text-amber-800 dark:text-amber-200">
                  {item.history_id
                    ? t("dashboard.handoffs.openConversation")
                    : t("dashboard.handoffs.openUser")}
                </div>
              </button>
            )
          })}
        </CardContent>
      ) : (
        <CardContent className="pt-0">
          <PermissionButton
            variant="outline"
            permission={Permission.WsUpdateResources}
            workspace={workspace}
            disabledTooltip={t("common.noPermission")}
            onClick={onOpenPrimary}
          >
            {t("dashboard.handoffs.openUserManagement")}
          </PermissionButton>
        </CardContent>
      )}
    </Card>
  )
}

const getConversationPreview = (
  history: History,
  automatedActionLabel: string
) => {
  const snippet = history.snippet?.trim()
  if (snippet?.startsWith("<tool")) {
    return automatedActionLabel
  }

  return snippet || history.externalUserId || history.agentName || "—"
}

const buildConversationHref = (
  workspaceId: string,
  params?: Record<string, string>
) => {
  const searchParams = new URLSearchParams({
    tab: "conversations",
    wid: workspaceId,
    ...params,
  })

  return `/workspaces/${workspaceId}?${searchParams.toString()}`
}

const LatestConversationRow = ({
  history,
  automatedActionLabel,
  locale,
  onClick,
}: {
  history: History
  automatedActionLabel: string
  locale?: string
  onClick: () => void
}) => (
  <button
    type="button"
    className="group grid min-h-[58px] w-full flex-1 grid-cols-[minmax(0,1fr)_auto] items-center gap-4 border-b border-slate-200 px-5 py-2 text-left transition last:border-b-0 hover:bg-slate-50 dark:border-slate-800 dark:hover:bg-slate-900"
    onClick={onClick}
  >
    <span className="min-w-0 space-y-0.5">
      <span className="block truncate text-sm font-medium leading-tight text-slate-900 dark:text-slate-100">
        {history.name}
      </span>
      <span className="block truncate text-xs leading-tight text-slate-500 dark:text-slate-400">
        {getConversationPreview(history, automatedActionLabel)}
      </span>
    </span>
    <span className="flex min-w-[9.5rem] shrink-0 items-center justify-end gap-2 text-right text-xs font-medium tabular-nums text-slate-500 dark:text-slate-400">
      <span className="whitespace-nowrap">
        {formatActivityTime(history.updatedAt || history.createdAt, locale)}
      </span>
      <ArrowRight className="h-4 w-4 shrink-0 text-slate-400 transition group-hover:translate-x-0.5 group-hover:text-slate-600 dark:text-slate-500 dark:group-hover:text-slate-300" />
    </span>
  </button>
)

const LaunchReadinessCard = ({
  title,
  description,
  summary,
  steps,
  primaryAction,
  workspace,
  isLoading,
}: {
  title: string
  description: string
  summary: Array<{ label: string; value: string | number }>
  steps: LaunchReadinessStep[]
  primaryAction?: LaunchReadinessAction
  workspace: Workspace
  isLoading: boolean
}) => {
  const { t } = useTranslation()

  return (
    <Card className="border-slate-200 bg-white shadow-xs dark:border-slate-800 dark:bg-slate-950">
      <CardContent className="p-0">
        <div className="grid gap-0 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.2fr)]">
          <div className="border-b border-slate-200 p-6 lg:border-b-0 lg:border-r dark:border-slate-800">
            <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-md bg-primary/10 text-primary">
              <Rocket className="h-5 w-5" />
            </div>
            <div className="space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="text-lg font-semibold text-slate-950 dark:text-slate-50">
                  {title}
                </h2>
                <Badge variant="outline" className="font-medium">
                  {isLoading
                    ? "..."
                    : steps.filter((step) => step.state === "complete").length}
                  /{steps.length}
                </Badge>
              </div>
              <p className="max-w-xl text-sm leading-6 text-slate-600 dark:text-slate-400">
                {description}
              </p>
            </div>

            <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-3">
              {summary.map((item) => (
                <div
                  key={item.label}
                  className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 dark:border-slate-800 dark:bg-slate-900/60"
                >
                  <div className="text-lg font-semibold leading-none text-slate-950 dark:text-slate-50">
                    {isLoading ? "—" : item.value}
                  </div>
                  <div className="mt-1 text-xs leading-tight text-slate-500 dark:text-slate-400">
                    {item.label}
                  </div>
                </div>
              ))}
            </div>

            {primaryAction ? (
              <div className="mt-5">
                {primaryAction.requiresWorkspaceEdit ? (
                  <PermissionButton
                    variant={primaryAction.variant}
                    permission={Permission.WsUpdateResources}
                    workspace={workspace}
                    disabledTooltip={t("common.noPermission")}
                    onClick={primaryAction.onClick}
                  >
                    {primaryAction.label}
                  </PermissionButton>
                ) : (
                  <Button
                    variant={primaryAction.variant}
                    onClick={primaryAction.onClick}
                  >
                    {primaryAction.label}
                  </Button>
                )}
              </div>
            ) : null}
          </div>

          <div className="p-6">
            {isLoading ? (
              <div className="space-y-3">
                {Array.from({ length: 3 }).map((_, index) => (
                  <Skeleton key={index} className="h-14 w-full" />
                ))}
              </div>
            ) : (
              <ol className="space-y-3">
                {steps.map((step) => {
                  const isComplete = step.state === "complete"
                  const isActive = step.state === "active"
                  const StepIcon = isComplete ? CheckCircle2 : Circle

                  return (
                    <li
                      key={step.id}
                      data-state={step.state}
                      className={`flex gap-3 rounded-md border p-3 ${
                        isActive
                          ? "border-primary/40 bg-primary/5"
                          : "border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-950"
                      }`}
                    >
                      <StepIcon
                        className={`mt-0.5 h-5 w-5 shrink-0 ${
                          isComplete
                            ? "text-emerald-600"
                            : isActive
                              ? "text-primary"
                              : "text-slate-400 dark:text-slate-500"
                        }`}
                      />
                      <div className="min-w-0">
                        <p className="text-sm font-medium leading-5 text-slate-950 dark:text-slate-50">
                          {step.title}
                        </p>
                        <p className="mt-1 text-sm leading-5 text-slate-600 dark:text-slate-400">
                          {step.description}
                        </p>
                        {step.linkUrl ? (
                          <a
                            href={step.linkUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-1.5 flex max-w-full items-center gap-1.5 font-mono text-xs text-slate-700 underline-offset-2 hover:underline dark:text-slate-300"
                          >
                            <span className="truncate">{step.linkUrl}</span>
                            <ExternalLink className="h-3.5 w-3.5 shrink-0" />
                          </a>
                        ) : null}
                      </div>
                    </li>
                  )
                })}
              </ol>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

const UsageAnalyticsEmptyCard = ({
  title,
  description,
}: {
  title: string
  description: string
}) => {
  return (
    <Card className="h-full min-h-[220px] border-slate-200 bg-white shadow-xs dark:border-slate-800 dark:bg-slate-950">
      <CardContent className="flex h-full flex-col items-start justify-center p-6">
        <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-md bg-slate-100 text-slate-500 dark:bg-slate-900 dark:text-slate-400">
          <BarChart3 className="h-5 w-5" />
        </div>
        <div className="max-w-md space-y-2">
          <h3 className="text-base font-semibold text-slate-950 dark:text-slate-50">
            {title}
          </h3>
          <p className="text-sm leading-6 text-slate-600 dark:text-slate-400">
            {description}
          </p>
        </div>
      </CardContent>
    </Card>
  )
}

const DashboardPage = () => {
  const { t, i18n } = useTranslation()
  const translationRef = useRef(t)
  translationRef.current = t
  const locale = i18n.resolvedLanguage
  const navigate = useNavigate()
  const { alertToast, successToast } = useToast()
  const [searchParams, setSearchParams] = useSearchParams()
  const [currentOrganization, workspaces] = useStore((state) => [
    state.currentOrganization,
    state.workspaces,
  ])
  const clientChannelDomain = useStore(
    (state) => state.sysConfig.clientChannelDomain
  )
  const [range, setRange] = useState<RangeOption>(7)
  const [dashboardData, setDashboardData] =
    useState<WorkspaceDashboardData | null>(null)
  const [resolvedMetricsScopeKey, setResolvedMetricsScopeKey] = useState<
    string | null
  >(null)
  const [channelReadinessSummary, setChannelReadinessSummary] =
    useState<ClientChannelReadinessSummary | null>(null)
  const [latestClientTrafficAt, setLatestClientTrafficAt] = useState<
    string | null
  >(null)
  const [hasEverClientTraffic, setHasEverClientTraffic] = useState(false)
  const [readinessErrorMessage, setReadinessErrorMessage] = useState<
    string | null
  >(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [selectedCreditUsageDate, setSelectedCreditUsageDate] = useState<
    string | null
  >(null)
  const [creditUsageModalOpen, setCreditUsageModalOpen] = useState(false)
  const [creditUsage, setCreditUsage] = useState<ReturnType<
    typeof transformCreditUsage
  > | null>(null)
  const [resolvedCreditUsageScopeKey, setResolvedCreditUsageScopeKey] =
    useState<string | null>(null)
  const [creditUsageErrorMessage, setCreditUsageErrorMessage] = useState<
    string | null
  >(null)
  const [
    improveFeedbackConversationCount,
    setImproveFeedbackConversationCount,
  ] = useState(0)
  const [feedbackStatus, setFeedbackStatus] =
    useState<DashboardRequestStatus>("loading")
  const [feedbackErrorMessage, setFeedbackErrorMessage] = useState<
    string | null
  >(null)
  const [latestConversations, setLatestConversations] = useState<History[]>([])
  const [latestConversationsStatus, setLatestConversationsStatus] =
    useState<DashboardRequestStatus>("loading")
  const [latestConversationsErrorMessage, setLatestConversationsErrorMessage] =
    useState<string | null>(null)
  const [evaluationCaseCount, setEvaluationCaseCount] = useState(0)
  const [evaluationResolution, setEvaluationResolution] = useState<{
    scopeKey: string
    status: DashboardRequestStatus
  } | null>(null)
  const [handoffSummary, setHandoffSummary] =
    useState<HumanHandoffSummary | null>(null)
  const [handoffSummaryErrorMessage, setHandoffSummaryErrorMessage] = useState<
    string | null
  >(null)
  const [readinessResolution, setReadinessResolution] = useState({
    channelSummary: false,
    clientTraffic: false,
    handoff: false,
  })

  const workspaceParam = searchParams.get("wid") || ""
  const firstWorkspaceId = workspaces[0]?.id ?? ""
  const activeWorkspace =
    workspaces.find((workspace) => workspace.id === workspaceParam) ||
    workspaces[0]
  const activeWorkspaceId = activeWorkspace?.id || firstWorkspaceId
  const metricsScopeKey = `${currentOrganization?.id ?? ""}:${activeWorkspaceId}:${range}`
  const activeWorkspaceLabel =
    activeWorkspace?.name ?? t("dashboard.selectors.workspacePlaceholder")
  const conversationHref = buildConversationHref(activeWorkspaceId)
  const improveFeedbackConversationHref = buildConversationHref(
    activeWorkspaceId,
    {
      fb: "improve_feedback",
    }
  )
  const operationsHref = `/workspaces/${activeWorkspaceId}?tab=form-submissions`
  const channelsHref = `/workspaces/${activeWorkspaceId}?tab=channels`
  const agentsHref = `/workspaces/${activeWorkspaceId}?tab=edit-agents`

  const { status: dashboardStatus, fetchFn: getDashboardMetrics } =
    useGetWorkspaceDashboardMetricsApi(activeWorkspaceId)
  const { status: creditUsageStatus, fetchFn: getCreditUsage } =
    useGetOrganizationCreditUsageApi(currentOrganization?.id || "")
  const { fetchFn: getChannelReadinessSummary } =
    useGetClientChannelReadinessSummaryApi(
      currentOrganization?.id || "",
      activeWorkspaceId
    )
  const { fetchFn: getClientTraffic } = useListClientUsersApi(
    currentOrganization?.id || "",
    activeWorkspaceId
  )
  const { fetchFn: getFeedbackHistories } = useGetHistoriesApi()
  const { status: latestHistoriesStatus, fetchFn: getLatestHistories } =
    useGetHistoriesApi()
  const { fetchFn: getHandoffSummary } = useGetHumanHandoffSummaryApi(
    currentOrganization?.id || "",
    activeWorkspaceId
  )
  const agentCount = channelReadinessSummary?.agent_count ?? 0
  const publishedAgentCount =
    channelReadinessSummary?.published_agent_count ?? 0
  const hasPublishedAgent = publishedAgentCount > 0
  const publishedChannelCount =
    channelReadinessSummary?.published_channel_count ?? 0
  const confirmedReadyChannelCount =
    channelReadinessSummary?.confirmed_ready_channel_count ?? 0
  const hasAgents = agentCount > 0
  const hasReadyChannel = confirmedReadyChannelCount > 0
  const launchReadinessLifecycleState = channelReadinessSummary
    ? resolveLaunchReadinessLifecycleState(channelReadinessSummary)
    : null
  const evaluationAgentId =
    channelReadinessSummary?.canonical_agent_ids.length === 1
      ? channelReadinessSummary.canonical_agent_ids[0]
      : undefined
  const evaluationScopeKey = evaluationAgentId
    ? `${currentOrganization?.id ?? ""}:${activeWorkspaceId}:${evaluationAgentId}`
    : ""
  const { status: evaluationCaseCountStatus, fetchFn: getEvaluationCaseCount } =
    useGetEvaluationCaseCountApi(evaluationAgentId)

  const isLoading = dashboardStatus === FetchStatus.Loading
  const isCoreReadinessLoading =
    resolvedMetricsScopeKey !== metricsScopeKey ||
    !readinessResolution.channelSummary ||
    !readinessResolution.clientTraffic
  const isHandoffSummaryLoading = !readinessResolution.handoff
  const isReadinessLoading =
    isCoreReadinessLoading ||
    (evaluationScopeKey
      ? evaluationResolution?.scopeKey !== evaluationScopeKey ||
        evaluationResolution.status === "loading"
      : false)
  const lifecycleErrorMessage = readinessErrorMessage || errorMessage
  const isLifecycleStateReady =
    !isCoreReadinessLoading && !lifecycleErrorMessage
  const canRenderStatusSignals =
    !isCoreReadinessLoading && !readinessErrorMessage
  const hasCreditUsageData = creditUsage?.hasData ?? false
  const canViewCreditUsageDetails =
    currentOrganization?.myRole === OrganizationRole.Owner ||
    currentOrganization?.myRole === OrganizationRole.Admin
  // Only web channels expose a shareable live URL; widgets embed via script instead
  const liveChannel = channelReadinessSummary?.live_web_channel
  const liveChannelUrl = useMemo(() => {
    if (!liveChannel) return ""
    const domain =
      clientChannelDomain?.trim().replace(/^\.+/, "") || "beta-web.codeer.ai"
    return `https://${liveChannel.last_published_slug || liveChannel.slug}.${domain}`
  }, [clientChannelDomain, liveChannel])
  const copyLiveLink = useCallback(() => {
    if (!liveChannelUrl) return
    const writeText = navigator.clipboard?.writeText
    if (!writeText) {
      alertToast({ title: t("dashboard.errors.copyLiveLinkFailed") })
      return
    }

    writeText
      .call(navigator.clipboard, liveChannelUrl)
      .then(() => {
        successToast({
          title: t("common.copied"),
          description: t("common.copiedToClipboard"),
        })
      })
      .catch(() => {
        alertToast({ title: t("dashboard.errors.copyLiveLinkFailed") })
      })
  }, [alertToast, liveChannelUrl, successToast, t])
  const needsReplyCount = handoffSummary?.needs_reply_count ?? 0
  const waitingOver30mCount = handoffSummary?.waiting_over_30m_count ?? 0
  const topWaitingItems = handoffSummary?.top_waiting_items ?? []
  const firstWaitingItem = topWaitingItems[0]
  const showHandoffPriority = needsReplyCount > 0
  const handoffTargetUrl = getHandoffTargetUrl(
    activeWorkspaceId,
    firstWaitingItem
  )
  const handleOpenHandoffItem = useCallback(
    (item: HumanHandoffSummaryItem) => {
      navigate(buildHandoffItemUrl(activeWorkspaceId, item))
    },
    [activeWorkspaceId, navigate]
  )
  const handleOpenPrimaryHandoff = useCallback(() => {
    navigate(handoffTargetUrl)
  }, [handoffTargetUrl, navigate])
  // Launch lifecycle precedence (loading/request errors are handled before it):
  //   no Agent                                      -> Agent active / Create Agent
  //   draft Agent only                              -> Agent active / Open Agents
  //   published Agent, no published Channel         -> Channel active / Check Channels
  //   published Channel, health unavailable         -> Channel active / Check Channels
  //   published Channel, deterministically unhealthy -> Channel active / Check Channels
  //   healthy Channel, missing/ambiguous ownership  -> Channel active / Agent or Channel CTA
  //   healthy Channel + canonical published Agent   -> Channel complete / share or inspect
  // Published count, health, and ownership stay separate so uncertainty never
  // authorizes launch or collapses a published Channel into an unpublished one.
  // Two range-scoped traffic sources can disagree transiently:
  // `hasClientTraffic` catches today's conversations before the nightly rollup,
  // while `hasConversations` comes from the selected-range metrics. Historical
  // client traffic proves prior launch only; it must not make a dormant
  // workspace look currently active. Allowed brief/checklist combinations:
  //   no Agent                         → lifecycle brief + checklist
  //   no traffic                       → lifecycle brief + checklist
  //   traffic, handoff waiting         → brief "handoff"   (no checklist)
  //   traffic, feedback pending        → brief "attention" (no checklist)
  //   traffic, no feedback             → brief "active"    (no checklist)
  const hasConversations = (dashboardData?.cards?.historiesCreated ?? 0) > 0
  const latestClientTrafficDate = latestClientTrafficAt?.slice(0, 10)
  const hasClientTraffic = Boolean(
    latestClientTrafficDate &&
    dashboardData?.rangeStart &&
    dashboardData.rangeEnd &&
    latestClientTrafficDate >= dashboardData.rangeStart &&
    latestClientTrafficDate <= dashboardData.rangeEnd
  )
  const hasTraffic = hasClientTraffic || hasConversations
  const launchReadinessSteps = useMemo<LaunchReadinessStep[]>(() => {
    const isAgentComplete = publishedAgentCount > 0
    const isChannelComplete = launchReadinessLifecycleState === "ready"
    const channelDescription = (() => {
      if (publishedChannelCount === 0) {
        return t("dashboard.launchReadiness.steps.channel.todo")
      }
      if (launchReadinessLifecycleState === "agentUnpublished") {
        return t(
          "dashboard.launchReadiness.steps.channel.publishedBlockedByAgent",
          { count: publishedChannelCount }
        )
      }
      if (launchReadinessLifecycleState === "channelUnavailable") {
        return t("dashboard.launchReadiness.steps.channel.healthUnavailable", {
          count: publishedChannelCount,
        })
      }
      if (launchReadinessLifecycleState === "channelUnhealthy") {
        return t("dashboard.launchReadiness.steps.channel.unhealthy", {
          count: publishedChannelCount,
        })
      }
      if (launchReadinessLifecycleState === "ownershipAmbiguous") {
        return t("dashboard.launchReadiness.steps.channel.ownershipAmbiguous", {
          count: publishedChannelCount,
        })
      }
      if (launchReadinessLifecycleState === "ownershipMissing") {
        return t("dashboard.launchReadiness.steps.channel.ownershipMissing", {
          count: publishedChannelCount,
        })
      }
      return t("dashboard.launchReadiness.steps.channel.done", {
        count: publishedChannelCount,
      })
    })()

    return [
      {
        id: "agent",
        title: t("dashboard.launchReadiness.steps.agent.title"),
        description: isAgentComplete
          ? t("dashboard.launchReadiness.steps.agent.done", {
              count: publishedAgentCount,
            })
          : hasAgents
            ? t("dashboard.launchReadiness.steps.agent.publishTodo", {
                count: agentCount,
              })
            : t("dashboard.launchReadiness.steps.agent.todo"),
        state: isAgentComplete ? "complete" : "active",
      },
      {
        id: "channel",
        title: t("dashboard.launchReadiness.steps.channel.title"),
        description: channelDescription,
        state: isChannelComplete
          ? "complete"
          : isAgentComplete
            ? "active"
            : "pending",
      },
      {
        id: "usage",
        title: t("dashboard.launchReadiness.steps.usage.title"),
        description: hasTraffic
          ? t("dashboard.launchReadiness.steps.usage.done")
          : hasReadyChannel
            ? t("dashboard.launchReadiness.steps.usage.noTraffic")
            : t("dashboard.launchReadiness.steps.usage.todo"),
        state: hasTraffic ? "complete" : hasReadyChannel ? "active" : "pending",
        linkUrl: !hasTraffic && liveChannelUrl ? liveChannelUrl : undefined,
      },
    ]
  }, [
    agentCount,
    hasAgents,
    hasTraffic,
    hasReadyChannel,
    launchReadinessLifecycleState,
    liveChannelUrl,
    publishedAgentCount,
    publishedChannelCount,
    t,
  ])
  const showLaunchReadiness =
    isLifecycleStateReady &&
    !showHandoffPriority &&
    !hasTraffic &&
    !hasEverClientTraffic &&
    launchReadinessSteps.some((step) => step.state !== "complete")
  const showPostLaunchSections = isLifecycleStateReady && !showLaunchReadiness
  const needsEvaluationCases =
    isLifecycleStateReady &&
    hasTraffic &&
    evaluationResolution?.scopeKey === evaluationScopeKey &&
    evaluationResolution.status === "success" &&
    evaluationCaseCountStatus === FetchStatus.Success &&
    evaluationCaseCount === 0 &&
    Boolean(evaluationAgentId)
  const evaluationCasesHref = evaluationAgentId
    ? `/workspaces/${activeWorkspaceId}/agents/${evaluationAgentId}?tab=evaluation`
    : ""
  const launchReadinessAction = useMemo<
    LaunchReadinessAction | undefined
  >(() => {
    if (launchReadinessLifecycleState === "noAgent") {
      return {
        label: t("dashboard.launchReadiness.actions.createAgent"),
        onClick: () => navigate(agentsHref),
        variant: "outline",
        requiresWorkspaceEdit: true,
      }
    }
    if (launchReadinessLifecycleState === "agentUnpublished") {
      return {
        label: t("dashboard.launchReadiness.actions.publishAgent"),
        onClick: () => navigate(agentsHref),
        variant: "outline",
        requiresWorkspaceEdit: true,
      }
    }
    if (launchReadinessLifecycleState === "ownershipAmbiguous") {
      return {
        label: t("dashboard.launchReadiness.actions.reviewAgents"),
        onClick: () => navigate(agentsHref),
        variant: "outline",
        requiresWorkspaceEdit: true,
      }
    }
    if (!hasReadyChannel) {
      return {
        label: t(
          publishedChannelCount > 0
            ? "dashboard.launchReadiness.actions.openChannels"
            : "dashboard.launchReadiness.actions.publishChannel"
        ),
        onClick: () => navigate(channelsHref),
        variant: "outline",
        requiresWorkspaceEdit: true,
      }
    }
    if (liveChannelUrl) {
      // The brief's "Share live link" is the solid primary for this state;
      // keep the checklist's duplicate copy action as a quiet outline.
      return {
        label: t("dashboard.launchReadiness.actions.copyLiveLink"),
        onClick: copyLiveLink,
        variant: "outline",
      }
    }
    return undefined
  }, [
    agentsHref,
    channelsHref,
    copyLiveLink,
    hasReadyChannel,
    launchReadinessLifecycleState,
    liveChannelUrl,
    navigate,
    publishedChannelCount,
    t,
  ])
  const emptyUsageLabel = useMemo(() => {
    if (hasTraffic) {
      return t("dashboard.emptyReasons.noTraffic")
    }
    if (launchReadinessLifecycleState === "noAgent") {
      return t("dashboard.emptyReasons.noAgent")
    }
    if (launchReadinessLifecycleState === "agentUnpublished") {
      return t("dashboard.emptyReasons.noPublishedAgent")
    }
    if (launchReadinessLifecycleState === "channelUnavailable") {
      return t("dashboard.emptyReasons.channelHealthUnavailable")
    }
    if (launchReadinessLifecycleState === "channelUnhealthy") {
      return t("dashboard.emptyReasons.publishedChannelUnhealthy")
    }
    if (
      launchReadinessLifecycleState === "ownershipAmbiguous" ||
      launchReadinessLifecycleState === "ownershipMissing"
    ) {
      return t("dashboard.emptyReasons.channelOwnership")
    }
    if (launchReadinessLifecycleState === "noChannel") {
      return t("dashboard.emptyReasons.noPublishedChannel")
    }
    return t("dashboard.emptyReasons.noTraffic")
  }, [hasTraffic, launchReadinessLifecycleState, t])
  // Form charts can be empty while general traffic exists; explain the right gap.
  const emptyFormsLabel = hasTraffic
    ? t("dashboard.emptyReasons.noFormActivity")
    : emptyUsageLabel
  const usageEmptyTitle =
    hasTraffic || hasEverClientTraffic
      ? t("dashboard.analyticsEmpty.rangeTitle")
      : t("dashboard.analyticsEmpty.title")
  const showUsageCharts = !dashboardData || dashboardData.hasData
  const creditUsageChartData = useMemo(() => {
    if (!creditUsage?.hasData) return []
    return buildChartData(creditUsage.points, range, locale)
  }, [creditUsage, locale, range])
  const creditUsageDateRange = useMemo(() => {
    if (!dashboardData?.rangeStart || !dashboardData?.rangeEnd) {
      return null
    }
    return {
      startDate: dashboardData.rangeStart,
      endDate: dashboardData.rangeEnd,
    }
  }, [dashboardData?.rangeEnd, dashboardData?.rangeStart])
  const creditUsageScopeKey = creditUsageDateRange
    ? `${currentOrganization?.id ?? ""}:${activeWorkspaceId}:${creditUsageDateRange.startDate}:${creditUsageDateRange.endDate}`
    : null
  const isCreditUsageResolved = Boolean(
    creditUsageScopeKey && resolvedCreditUsageScopeKey === creditUsageScopeKey
  )
  const isCreditUsageLoading =
    !creditUsageScopeKey ||
    !isCreditUsageResolved ||
    creditUsageStatus === FetchStatus.Loading
  const canOpenCreditUsageDetails =
    canViewCreditUsageDetails &&
    isCreditUsageResolved &&
    !creditUsageErrorMessage

  const chartData = useMemo(() => {
    if (!dashboardData || !dashboardData.hasData) {
      return {
        unique: [],
        histories: [],
        formCalls: { data: [], seriesMeta: [] },
        formSubmissions: { data: [], seriesMeta: [] },
      }
    }
    return {
      unique: buildChartData(
        dashboardData.charts.dailyUniqueClientUsers,
        dashboardData.rangeDays,
        locale
      ),
      histories: buildChartData(
        dashboardData.charts.dailyHistoriesCreated,
        dashboardData.rangeDays,
        locale
      ),
      formCalls: buildSeriesChartData(
        dashboardData.charts.dailyFormToolCallsByAgent,
        dashboardData.rangeDays,
        locale
      ),
      formSubmissions: buildSeriesChartData(
        dashboardData.charts.dailyFormSubmissionsByAgent,
        dashboardData.rangeDays,
        locale
      ),
    }
  }, [dashboardData, locale])

  const cards: WorkspaceDashboardCards | null = dashboardData?.cards ?? null

  useEffect(() => {
    const hasWorkspaceParam = Boolean(workspaceParam)
    const paramMatchesWorkspace = workspaces.some(
      (workspace) => workspace.id === workspaceParam
    )
    const shouldSetDefault =
      (!hasWorkspaceParam || !paramMatchesWorkspace) && firstWorkspaceId

    if (shouldSetDefault) {
      const nextParams = new URLSearchParams(searchParams)
      nextParams.set("wid", firstWorkspaceId)
      setSearchParams(nextParams, { replace: true })
    }
  }, [
    workspaceParam,
    workspaces,
    firstWorkspaceId,
    searchParams,
    setSearchParams,
  ])

  useEffect(() => {
    if (!activeWorkspaceId) return

    let ignore = false
    const controller = new AbortController()

    getDashboardMetrics({
      signal: controller.signal,
      searchParams: new URLSearchParams({
        range: range.toString(),
      }),
      callbackFn: (response) => {
        if (ignore) return
        if (!response?.data) {
          setErrorMessage(
            translationRef.current("dashboard.errors.loadFailed.description")
          )
          setResolvedMetricsScopeKey(metricsScopeKey)
          return
        }
        setDashboardData(transformWorkspaceDashboardData(response.data))
        setErrorMessage(null)
        setResolvedMetricsScopeKey(metricsScopeKey)
      },
    }).catch((err) => {
      if (ignore) return
      console.error("Failed to fetch dashboard metrics", err)
      setErrorMessage(
        getUserFacingApiErrorMessage(
          err,
          translationRef.current,
          translationRef.current("dashboard.errors.loadFailed.description")
        )
      )
      setResolvedMetricsScopeKey(metricsScopeKey)
    })

    return () => {
      ignore = true
      controller.abort()
    }
  }, [activeWorkspaceId, range, getDashboardMetrics, metricsScopeKey])

  useLayoutEffect(() => {
    setDashboardData(null)
    setErrorMessage(null)
    setResolvedMetricsScopeKey(null)
  }, [metricsScopeKey])

  useLayoutEffect(() => {
    setChannelReadinessSummary(null)
    setLatestClientTrafficAt(null)
    setReadinessErrorMessage(null)
    setCreditUsage(null)
    setImproveFeedbackConversationCount(0)
    setFeedbackStatus("loading")
    setFeedbackErrorMessage(null)
    setLatestConversations([])
    setLatestConversationsStatus("loading")
    setLatestConversationsErrorMessage(null)
    setEvaluationCaseCount(0)
    setHandoffSummary(null)
    setHandoffSummaryErrorMessage(null)
    setReadinessResolution({
      channelSummary: false,
      clientTraffic: false,
      handoff: false,
    })
  }, [activeWorkspaceId, currentOrganization?.id])

  useEffect(() => {
    if (!currentOrganization?.id || !activeWorkspaceId) return

    let ignore = false
    const controller = new AbortController()

    const handleReadinessError = (
      source: "channelSummary" | "clientTraffic",
      label: string,
      err: unknown
    ) => {
      if (ignore) return
      console.error(`Failed to fetch dashboard ${label}`, err)
      setReadinessResolution((current) => ({
        ...current,
        [source]: true,
      }))
      setReadinessErrorMessage(
        getUserFacingApiErrorMessage(
          err,
          translationRef.current,
          translationRef.current("dashboard.errors.loadFailed.description")
        )
      )
    }

    getChannelReadinessSummary({
      signal: controller.signal,
      callbackFn: (response) => {
        if (ignore) return
        if (!response?.data) {
          setChannelReadinessSummary(null)
          setReadinessErrorMessage(
            translationRef.current("dashboard.errors.loadFailed.description")
          )
          setReadinessResolution((current) => ({
            ...current,
            channelSummary: true,
          }))
          return
        }
        setChannelReadinessSummary(response.data)
        setReadinessResolution((current) => ({
          ...current,
          channelSummary: true,
        }))
      },
    }).catch((err) => {
      handleReadinessError("channelSummary", "channel readiness", err)
    })
    setLatestClientTrafficAt(null)
    setHasEverClientTraffic(false)
    getClientTraffic({
      signal: controller.signal,
      searchParams: new URLSearchParams({
        has_conversation: "true",
        order_by: "-last_conversation_at",
        limit: "1",
        offset: "0",
      }),
      callbackFn: (response) => {
        if (ignore) return
        setHasEverClientTraffic(
          Boolean(response?.data?.length || response?.pagination?.total_records)
        )
        setLatestClientTrafficAt(
          response?.data?.[0]?.last_conversation_at ?? null
        )
        setReadinessResolution((current) => ({
          ...current,
          clientTraffic: true,
        }))
      },
    }).catch((err) => {
      handleReadinessError("clientTraffic", "client traffic", err)
    })

    return () => {
      ignore = true
      controller.abort()
    }
  }, [
    activeWorkspaceId,
    currentOrganization?.id,
    getChannelReadinessSummary,
    getClientTraffic,
  ])

  useEffect(() => {
    if (!currentOrganization?.id || !activeWorkspaceId) return

    let ignore = false
    const controller = new AbortController()
    setFeedbackStatus("loading")
    setFeedbackErrorMessage(null)

    getFeedbackHistories({
      signal: controller.signal,
      searchParams: new URLSearchParams({
        limit: "1",
        offset: "0",
        organization_id: currentOrganization.id,
        workspace_id: activeWorkspaceId,
        order_by: "desc",
        feedback_filter: "improve_feedback",
      }),
      callbackFn: (response) => {
        if (ignore) return
        setImproveFeedbackConversationCount(
          response?.pagination?.total_records ?? response?.data?.length ?? 0
        )
        setFeedbackStatus("success")
        setFeedbackErrorMessage(null)
      },
    }).catch((err) => {
      if (ignore) return
      console.error("Failed to fetch dashboard feedback histories", err)
      setFeedbackStatus("error")
      setFeedbackErrorMessage(
        getUserFacingApiErrorMessage(
          err,
          translationRef.current,
          translationRef.current("dashboard.errors.loadFailed.description")
        )
      )
    })

    return () => {
      ignore = true
      controller.abort()
    }
  }, [activeWorkspaceId, currentOrganization?.id, getFeedbackHistories])

  useEffect(() => {
    if (!currentOrganization?.id || !activeWorkspaceId) {
      setHandoffSummary(null)
      setHandoffSummaryErrorMessage(null)
      return
    }

    let ignore = false
    const controller = new AbortController()

    setHandoffSummary(null)
    getHandoffSummary({
      signal: controller.signal,
      callbackFn: (response) => {
        if (ignore) return
        setHandoffSummary(response?.data ?? null)
        setHandoffSummaryErrorMessage(null)
        setReadinessResolution((current) => ({
          ...current,
          handoff: true,
        }))
      },
    }).catch((err) => {
      if (ignore) return
      console.error("Failed to fetch human handoff summary", err)
      setHandoffSummary(null)
      setReadinessResolution((current) => ({
        ...current,
        handoff: true,
      }))
      setHandoffSummaryErrorMessage(
        getUserFacingApiErrorMessage(
          err,
          translationRef.current,
          translationRef.current("dashboard.errors.loadFailed.description")
        )
      )
    })

    return () => {
      ignore = true
      controller.abort()
    }
  }, [activeWorkspaceId, currentOrganization?.id, getHandoffSummary])

  useEffect(() => {
    if (!currentOrganization?.id || !activeWorkspaceId) return

    let ignore = false
    const controller = new AbortController()
    setLatestConversationsStatus("loading")
    setLatestConversationsErrorMessage(null)

    getLatestHistories({
      signal: controller.signal,
      searchParams: new URLSearchParams({
        limit: "5",
        offset: "0",
        organization_id: currentOrganization.id,
        workspace_id: activeWorkspaceId,
        order_by: "desc",
      }),
      callbackFn: (response) => {
        if (ignore) return
        setLatestConversations(
          response?.data?.map(historyResponseToHistory) ?? []
        )
        setLatestConversationsStatus("success")
        setLatestConversationsErrorMessage(null)
      },
    }).catch((err) => {
      if (ignore) return
      console.error("Failed to fetch dashboard latest histories", err)
      setLatestConversationsStatus("error")
      setLatestConversationsErrorMessage(
        getUserFacingApiErrorMessage(
          err,
          translationRef.current,
          translationRef.current("dashboard.errors.loadFailed.description")
        )
      )
    })

    return () => {
      ignore = true
      controller.abort()
    }
  }, [activeWorkspaceId, currentOrganization?.id, getLatestHistories])

  useLayoutEffect(() => {
    setCreditUsage(null)
    setCreditUsageErrorMessage(null)
    setResolvedCreditUsageScopeKey(null)
  }, [creditUsageScopeKey])

  useEffect(() => {
    if (
      !currentOrganization?.id ||
      !activeWorkspaceId ||
      !creditUsageDateRange ||
      !creditUsageScopeKey
    ) {
      setCreditUsage(null)
      return
    }

    let ignore = false
    const controller = new AbortController()

    getCreditUsage({
      signal: controller.signal,
      searchParams: new URLSearchParams({
        start_date: creditUsageDateRange.startDate,
        end_date: creditUsageDateRange.endDate,
        granularity: "daily",
        workspace_id: activeWorkspaceId,
      }),
      callbackFn: (response) => {
        if (ignore) return
        if (!response?.data) {
          setCreditUsage(null)
          setCreditUsageErrorMessage(
            translationRef.current("dashboard.errors.loadFailed.description")
          )
          setResolvedCreditUsageScopeKey(creditUsageScopeKey)
          return
        }
        setCreditUsage(transformCreditUsage(response.data))
        setCreditUsageErrorMessage(null)
        setResolvedCreditUsageScopeKey(creditUsageScopeKey)
      },
    }).catch((err) => {
      if (ignore) return
      console.error("Failed to fetch credit usage", err)
      const message = getUserFacingApiErrorMessage(
        err,
        translationRef.current,
        translationRef.current("dashboard.errors.loadFailed.description")
      )
      setCreditUsage(null)
      setCreditUsageErrorMessage(message)
      setResolvedCreditUsageScopeKey(creditUsageScopeKey)
      alertToast({
        title: translationRef.current("dashboard.errors.loadFailed.title"),
        description: message,
        showSupportAction: true,
      })
    })

    return () => {
      ignore = true
      controller.abort()
    }
  }, [
    activeWorkspaceId,
    alertToast,
    creditUsageDateRange,
    creditUsageScopeKey,
    currentOrganization?.id,
    getCreditUsage,
  ])

  useLayoutEffect(() => {
    setEvaluationCaseCount(0)
    setEvaluationResolution(
      evaluationScopeKey
        ? { scopeKey: evaluationScopeKey, status: "loading" }
        : null
    )
  }, [evaluationScopeKey])

  useEffect(() => {
    if (!evaluationAgentId || !evaluationScopeKey) return

    let ignore = false
    const controller = new AbortController()

    getEvaluationCaseCount({
      signal: controller.signal,
      callbackFn: (response) => {
        if (ignore) return
        setEvaluationCaseCount(response?.data?.total_case_count ?? 0)
        setEvaluationResolution({
          scopeKey: evaluationScopeKey,
          status: "success",
        })
      },
    }).catch((err: unknown) => {
      if (ignore) return
      console.error("Failed to fetch dashboard evaluation cases", err)
      setEvaluationResolution({
        scopeKey: evaluationScopeKey,
        status: "error",
      })
    })

    return () => {
      ignore = true
      controller.abort()
    }
  }, [evaluationAgentId, evaluationScopeKey, getEvaluationCaseCount])

  const creditTotal = creditUsage?.totalCredits ?? 0
  const hasActiveUsers = hasClientTraffic || (cards?.uniqueClientUsers ?? 0) > 0
  const hasFormsActivity =
    (cards?.formSubmissions ?? 0) > 0 || (cards?.formToolCalls ?? 0) > 0
  // Form charts are machinery until a form has actually been used; hide the
  // whole row for workspaces with no form activity in the data.
  const hasFormChartSeries =
    chartData.formCalls.seriesMeta.length > 0 ||
    chartData.formSubmissions.seriesMeta.length > 0
  const showFormCharts =
    showUsageCharts && (hasFormChartSeries || hasFormsActivity)
  const isLatestConversationsLoading =
    latestConversationsStatus === "loading" ||
    latestHistoriesStatus === FetchStatus.Loading
  const feedbackSignalValue =
    feedbackStatus === "loading"
      ? "—"
      : feedbackStatus === "error"
        ? t("dashboard.activity.feedback.unavailable")
        : t("dashboard.activity.feedback.value", {
            displayCount: formatNumber(improveFeedbackConversationCount),
          })
  const feedbackSignalDetail =
    feedbackStatus === "loading"
      ? t("common.loading")
      : feedbackStatus === "error"
        ? (feedbackErrorMessage ??
          t("dashboard.activity.feedback.unavailableDetail"))
        : t("dashboard.activity.feedback.detail")
  // Handoff is the only proven blocker and stays primary even when its latest
  // conversation falls outside the selected traffic range. Forms and feedback
  // remain secondary because the backend cannot know whether an operator
  // already followed up.
  const briefState = !hasAgents
    ? "setup"
    : showHandoffPriority
      ? "handoff"
      : !hasTraffic
        ? launchReadinessLifecycleState === "ready" && !hasEverClientTraffic
          ? "launched"
          : launchReadinessLifecycleState === "agentUnpublished"
            ? "agentDraft"
            : launchReadinessLifecycleState === "channelUnavailable"
              ? "channelUnavailable"
              : launchReadinessLifecycleState === "channelUnhealthy"
                ? "channelUnhealthy"
                : launchReadinessLifecycleState === "ownershipAmbiguous" ||
                    launchReadinessLifecycleState === "ownershipMissing"
                  ? "channelOwnership"
                  : "quiet"
        : "active"
  const todayLabel = new Intl.DateTimeFormat(locale, {
    weekday: "long",
    month: "long",
    day: "numeric",
  }).format(new Date())
  const openCreditUsageDetails = () => {
    if (!creditUsageDateRange) {
      navigate(SETTINGS_ENDPOINTS.organizationPlan)
      return
    }

    const params = new URLSearchParams({
      credit_workspace_id: activeWorkspaceId,
      credit_start_date: creditUsageDateRange.startDate,
      credit_end_date: creditUsageDateRange.endDate,
      credit_granularity: "daily",
    })
    navigate(
      `${SETTINGS_ENDPOINTS.organizationPlan}?${params.toString()}#credit-usage-details`
    )
  }

  const recommendedAction = ((): {
    title: string
    description: string
    label: string
    href?: string
    onClick?: () => void
    icon?: ElementType
    requiresWorkspaceEdit?: boolean
  } | null => {
    if (!isLifecycleStateReady) return null
    if (!hasAgents) {
      return {
        title: t("dashboard.nextAction.createAgent.title"),
        description: t("dashboard.nextAction.createAgent.description"),
        label: t("dashboard.nextAction.createAgent.cta"),
        href: agentsHref,
        requiresWorkspaceEdit: true,
      }
    }
    if (showHandoffPriority) {
      return {
        title: t("dashboard.nextAction.handoff.title"),
        description: t("dashboard.nextAction.handoff.description", {
          count: needsReplyCount,
          displayCount: formatNumber(needsReplyCount),
        }),
        label: t("dashboard.nextAction.handoff.cta"),
        href: handoffTargetUrl,
        icon: UserRoundCheck,
        requiresWorkspaceEdit: true,
      }
    }
    if (launchReadinessLifecycleState === "agentUnpublished") {
      return {
        title: t("dashboard.nextAction.publishAgent.title"),
        description: t("dashboard.nextAction.publishAgent.description"),
        label: t("dashboard.nextAction.publishAgent.cta"),
        href: agentsHref,
        requiresWorkspaceEdit: true,
      }
    }
    if (!hasTraffic) {
      if (hasReadyChannel && liveChannelUrl) {
        return {
          title: t("dashboard.nextAction.shareChannel.title"),
          description: t("dashboard.nextAction.shareChannel.description"),
          label: t("dashboard.nextAction.shareChannel.cta"),
          onClick: copyLiveLink,
          icon: Copy,
        }
      }
      if (launchReadinessLifecycleState === "ownershipAmbiguous") {
        return {
          title: t("dashboard.nextAction.reviewAgents.title"),
          description: t("dashboard.nextAction.reviewAgents.description"),
          label: t("dashboard.nextAction.reviewAgents.cta"),
          href: agentsHref,
          requiresWorkspaceEdit: true,
        }
      }
      return {
        title: t("dashboard.nextAction.checkChannel.title"),
        description: t("dashboard.nextAction.checkChannel.description"),
        label: t("dashboard.nextAction.checkChannel.cta"),
        href: channelsHref,
        requiresWorkspaceEdit: true,
      }
    }
    if (needsEvaluationCases && evaluationCasesHref) {
      return {
        title: t("dashboard.postLaunchNotice.scenarios.title"),
        description: t("dashboard.postLaunchNotice.scenarios.description"),
        label: t("dashboard.postLaunchNotice.scenarios.cta"),
        href: evaluationCasesHref,
        icon: ClipboardCheck,
        requiresWorkspaceEdit: true,
      }
    }
    return {
      title: t("dashboard.nextAction.viewConversations.title"),
      description: t("dashboard.nextAction.viewConversations.description"),
      label: t("dashboard.nextAction.viewConversations.cta"),
      href: conversationHref,
    }
  })()
  const RecommendedActionIcon = recommendedAction?.icon ?? ArrowRight
  const handleRecommendedAction = () => {
    if (recommendedAction?.onClick) {
      recommendedAction.onClick()
      return
    }
    if (recommendedAction?.href) {
      navigate(recommendedAction.href)
    }
  }

  if (!currentOrganization) {
    return <ErrorPage message={t("dashboard.errors.noOrganization")} />
  }

  if (!activeWorkspaceId) {
    return <ErrorPage message={t("dashboard.errors.noWorkspace")} />
  }

  return (
    <div className="space-y-6 pb-8">
      <CreditUsageRecordsModal
        open={creditUsageModalOpen}
        onOpenChange={setCreditUsageModalOpen}
        organizationId={currentOrganization.id}
        selectedDate={selectedCreditUsageDate}
        workspaceId={activeWorkspaceId}
      />
      <Card className="gap-0 border-slate-200 bg-white py-0 shadow-xs dark:border-slate-800 dark:bg-slate-950">
        <CardContent className="space-y-6 p-6">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
            <div className="max-w-3xl space-y-3">
              <p className="text-xs font-medium tracking-wide text-slate-500 dark:text-slate-400">
                {t("dashboard.brief.eyebrow", { date: todayLabel })}
              </p>
              {isCoreReadinessLoading ? (
                <div className="space-y-3" aria-label={t("common.loading")}>
                  <Skeleton className="h-9 w-full max-w-2xl" />
                  <Skeleton className="h-6 w-full max-w-xl" />
                </div>
              ) : lifecycleErrorMessage ? (
                <div className="space-y-2">
                  <h1 className="max-w-4xl text-2xl font-semibold leading-tight text-slate-950 md:text-3xl dark:text-slate-50">
                    {t("dashboard.errors.loadFailed.title")}
                  </h1>
                  <p className="max-w-2xl text-base text-slate-600 dark:text-slate-400">
                    {lifecycleErrorMessage}
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  <h1 className="max-w-4xl text-2xl font-semibold leading-tight text-slate-950 md:text-3xl dark:text-slate-50">
                    {t(
                      `dashboard.brief.${briefState}.title`,
                      briefState === "handoff"
                        ? {
                            workspace: activeWorkspaceLabel,
                            count: needsReplyCount,
                            displayCount: formatNumber(needsReplyCount),
                          }
                        : {
                            workspace: activeWorkspaceLabel,
                          }
                    )}
                  </h1>
                  <p className="max-w-2xl text-base text-slate-600 dark:text-slate-400">
                    {t(
                      // Live traffic can precede the nightly metrics rollup;
                      // explain the lag instead of claiming flowing numbers.
                      briefState === "active" && !hasConversations
                        ? "dashboard.brief.active.descriptionAggregating"
                        : `dashboard.brief.${briefState}.description`,
                      {
                        workspace: activeWorkspaceLabel,
                        count: needsReplyCount,
                        displayCount: formatNumber(needsReplyCount),
                        range: t("dashboard.selectors.range", { days: range }),
                      }
                    )}
                  </p>
                </div>
              )}
            </div>

            <div className="flex flex-col gap-3 sm:flex-row lg:flex-col lg:items-stretch">
              <div className="flex rounded-md border border-slate-200 bg-white p-1 shadow-xs dark:border-slate-800 dark:bg-slate-900">
                {RANGE_OPTIONS.map((option) => (
                  <Button
                    key={option}
                    variant={range === option ? "default" : "ghost"}
                    size="sm"
                    className="px-3"
                    onClick={() => setRange(option)}
                  >
                    {t("dashboard.selectors.range", { days: option })}
                  </Button>
                ))}
              </div>
              {recommendedAction ? (
                recommendedAction.requiresWorkspaceEdit && activeWorkspace ? (
                  <PermissionButton
                    className="justify-between"
                    permission={Permission.WsUpdateResources}
                    workspace={activeWorkspace}
                    disabledTooltip={t("common.noPermission")}
                    onClick={handleRecommendedAction}
                  >
                    {recommendedAction.label}
                    <RecommendedActionIcon className="h-4 w-4" />
                  </PermissionButton>
                ) : (
                  <Button
                    className="justify-between"
                    onClick={handleRecommendedAction}
                  >
                    {recommendedAction.label}
                    <RecommendedActionIcon className="h-4 w-4" />
                  </Button>
                )
              ) : isCoreReadinessLoading ? (
                <Skeleton className="h-9 w-full" />
              ) : null}
            </div>
          </div>

          {cards && !showLaunchReadiness && canRenderStatusSignals ? (
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              <OpsSignal
                icon={hasPublishedAgent ? CheckCircle2 : AlertCircle}
                label={t("dashboard.status.agent.label")}
                value={
                  hasPublishedAgent
                    ? t("dashboard.status.agent.active")
                    : t("dashboard.status.agent.needsSetup")
                }
                detail={t("dashboard.status.agent.detail", {
                  count: cards.historiesCreated,
                  displayCount: formatNumber(cards.historiesCreated),
                  range: t("dashboard.selectors.range", { days: range }),
                })}
                onClick={() => navigate(agentsHref)}
                tone={hasPublishedAgent ? "good" : "attention"}
              />
              <OpsSignal
                icon={Send}
                label={t("dashboard.status.channel.label")}
                value={
                  hasActiveUsers
                    ? t("dashboard.status.channel.active")
                    : t("dashboard.status.channel.needsActivity")
                }
                detail={t("dashboard.status.channel.detail", {
                  count: cards.uniqueClientUsers,
                  displayCount: formatNumber(cards.uniqueClientUsers),
                })}
                onClick={() => navigate(channelsHref)}
                tone={hasActiveUsers ? "good" : "attention"}
              />
              <OpsSignal
                icon={MessageSquare}
                label={t("dashboard.activity.feedback.label")}
                value={feedbackSignalValue}
                detail={feedbackSignalDetail}
                onClick={() => navigate(improveFeedbackConversationHref)}
                tone={
                  feedbackStatus === "error" ||
                  (feedbackStatus === "success" &&
                    improveFeedbackConversationCount > 0)
                    ? "attention"
                    : "neutral"
                }
              />
              <OpsSignal
                icon={CreditCard}
                label={t("dashboard.status.credit.label")}
                value={
                  isCreditUsageLoading
                    ? "—"
                    : creditUsageErrorMessage
                      ? t("dashboard.status.credit.unavailable")
                      : t("dashboard.status.credit.value", {
                          displayCredits: formatCredits(creditTotal),
                        })
                }
                detail={
                  isCreditUsageLoading
                    ? t("common.loading")
                    : creditUsageErrorMessage
                      ? creditUsageErrorMessage
                      : creditTotal > 0
                        ? t("dashboard.status.credit.detail")
                        : t("dashboard.status.credit.noSpend")
                }
                onClick={
                  canOpenCreditUsageDetails ? openCreditUsageDetails : undefined
                }
                tone={
                  isCreditUsageLoading
                    ? "neutral"
                    : creditUsageErrorMessage
                      ? "attention"
                      : creditTotal > 0
                        ? "neutral"
                        : "good"
                }
              />
            </div>
          ) : isLoading || isCoreReadinessLoading ? (
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              {Array.from({ length: 4 }).map((_, idx) => (
                <Skeleton key={idx} className="h-[132px] w-full" />
              ))}
            </div>
          ) : null}
        </CardContent>
      </Card>

      {isLifecycleStateReady && activeWorkspace && (
        <HandoffPriorityPanel
          errorMessage={handoffSummaryErrorMessage}
          isLoading={isHandoffSummaryLoading}
          items={topWaitingItems}
          needsReplyCount={needsReplyCount}
          waitingOver30mCount={waitingOver30mCount}
          onOpenItem={handleOpenHandoffItem}
          onOpenPrimary={handleOpenPrimaryHandoff}
          workspace={activeWorkspace}
        />
      )}

      {readinessErrorMessage && (
        <Card className="border-amber-200 bg-amber-50 text-amber-900">
          <CardContent className="py-3 text-sm">
            {readinessErrorMessage}
          </CardContent>
        </Card>
      )}

      {!hasReadyChannel &&
        (channelReadinessSummary?.health_status === "unavailable" ||
          channelReadinessSummary?.external_health_coverage_status ===
            "incomplete") && (
          <Card className="border-amber-200 bg-amber-50 text-amber-900">
            <CardContent className="py-3 text-sm">
              {t("dashboard.errors.channelHealthUnavailable")}
            </CardContent>
          </Card>
        )}

      {!hasReadyChannel &&
        channelReadinessSummary?.health_status === "unhealthy" &&
        channelReadinessSummary.external_health_coverage_status !==
          "incomplete" && (
          <Card className="border-amber-200 bg-amber-50 text-amber-900">
            <CardContent className="py-3 text-sm">
              {t("dashboard.errors.publishedChannelUnhealthy")}
            </CardContent>
          </Card>
        )}

      {showLaunchReadiness && activeWorkspace && (
        <LaunchReadinessCard
          title={t("dashboard.launchReadiness.title")}
          description={t("dashboard.launchReadiness.description")}
          summary={[
            {
              label: t("dashboard.launchReadiness.summary.agents"),
              value: agentCount,
            },
            {
              label: t("dashboard.launchReadiness.summary.publishedChannels"),
              value: publishedChannelCount,
            },
            {
              label: t("dashboard.launchReadiness.steps.usage.title"),
              value: hasTraffic ? 1 : 0,
            },
          ]}
          steps={launchReadinessSteps}
          primaryAction={launchReadinessAction}
          workspace={activeWorkspace}
          isLoading={isReadinessLoading}
        />
      )}

      {isLifecycleStateReady && (
        <div className="grid items-stretch gap-4 xl:grid-cols-[minmax(0,1.15fr)_minmax(380px,0.85fr)]">
          {showUsageCharts ? (
            <ChartCard
              title={t("dashboard.charts.dailyHistories")}
              data={chartData.histories}
              color="#10B981"
              isLoading={isLoading}
              emptyLabel={emptyUsageLabel}
              seriesLabel={t("dashboard.charts.seriesConversations")}
              partialDate={dashboardData?.rangeEnd}
              chartHeight={220}
            />
          ) : (
            <UsageAnalyticsEmptyCard
              title={usageEmptyTitle}
              description={emptyUsageLabel}
            />
          )}

          <Card className="flex h-full flex-col overflow-hidden border-slate-200 bg-white shadow-xs dark:border-slate-800 dark:bg-slate-950">
            <CardHeader className="px-5 py-3">
              <div className="flex items-center justify-between gap-3">
                <CardTitle className="text-base font-semibold text-slate-900 dark:text-slate-50">
                  {t("dashboard.activity.latestConversations.title")}
                </CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 px-2"
                  onClick={() => navigate(conversationHref)}
                >
                  {t("dashboard.activity.latestConversations.viewAll")}
                </Button>
              </div>
            </CardHeader>
            <CardContent className="flex-1 px-0 pb-0">
              {latestConversationsErrorMessage ? (
                <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-4 text-sm text-amber-900 dark:border-amber-900/70 dark:bg-amber-950/30 dark:text-amber-200">
                  <p className="font-medium">
                    {t("dashboard.activity.latestConversations.unavailable")}
                  </p>
                  <p className="mt-1">{latestConversationsErrorMessage}</p>
                </div>
              ) : isLatestConversationsLoading ? (
                <div className="space-y-2">
                  {Array.from({ length: 3 }).map((_, idx) => (
                    <Skeleton key={idx} className="h-[60px] w-full" />
                  ))}
                </div>
              ) : latestConversations.length > 0 ? (
                <div className="flex h-full flex-col">
                  {latestConversations.slice(0, 4).map((history) => (
                    <LatestConversationRow
                      key={history.id}
                      history={history}
                      automatedActionLabel={t(
                        "dashboard.activity.latestConversations.automatedAction"
                      )}
                      locale={locale}
                      onClick={() =>
                        navigate(
                          buildConversationHref(activeWorkspaceId, {
                            hid: history.id.toString(),
                            focus: "history",
                          })
                        )
                      }
                    />
                  ))}
                </div>
              ) : (
                <div className="rounded-md border border-dashed border-slate-200 px-3 py-4 text-sm text-slate-500 dark:border-slate-800 dark:text-slate-400">
                  {t("dashboard.activity.latestConversations.empty")}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Pre-launch, the launch checklist owns this moment; attention and
          usage metrics would only restate zeros. */}
      {showPostLaunchSections && (
        <Card className="gap-4 border-slate-200 bg-white py-5 shadow-xs dark:border-slate-800 dark:bg-slate-950">
          <CardHeader>
            <CardTitle className="text-base font-semibold text-slate-900 dark:text-slate-50">
              {t("dashboard.attention.title")}
            </CardTitle>
          </CardHeader>
          <CardContent className="grid gap-0 md:grid-cols-2 md:gap-x-6">
            <ActivityRow
              label={t("dashboard.activity.feedback.label")}
              value={feedbackSignalValue}
              detail={feedbackSignalDetail}
              onClick={() => navigate(improveFeedbackConversationHref)}
            />
            <ActivityRow
              label={t("dashboard.activity.formSubmissions.label")}
              value={
                cards
                  ? t("dashboard.activity.formSubmissions.value", {
                      displayCount: formatNumber(cards.formSubmissions),
                    })
                  : "—"
              }
              detail={t("dashboard.activity.formSubmissions.detail")}
              onClick={() => navigate(operationsHref)}
            />
          </CardContent>
        </Card>
      )}

      {showPostLaunchSections && (
        <section className="space-y-3">
          <div>
            <h2 className="text-base font-semibold text-slate-900 dark:text-slate-50">
              {t("dashboard.usage.title")}
            </h2>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              {t("dashboard.usage.description", {
                workspace: activeWorkspaceLabel,
                range: t("dashboard.selectors.range", { days: range }),
              })}
            </p>
          </div>
          <div
            className={
              showUsageCharts ? "grid gap-4 lg:grid-cols-2" : "grid gap-4"
            }
          >
            <Card className="group h-full border-slate-200 bg-white shadow-xs transition hover:border-slate-300 hover:bg-slate-50/60 dark:border-slate-800 dark:bg-slate-950 dark:hover:border-slate-700 dark:hover:bg-slate-900/50">
              <CardHeader className="gap-2 pb-2">
                <div className="flex items-start justify-between gap-3">
                  <div className="space-y-1">
                    <CardTitle className="text-base font-semibold text-slate-800 transition group-hover:text-slate-700 dark:text-slate-200 dark:group-hover:text-slate-100">
                      {t("dashboard.charts.creditSpend")}
                    </CardTitle>
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      {isCreditUsageLoading
                        ? t("common.loading")
                        : creditUsageErrorMessage
                          ? t("dashboard.status.credit.unavailable")
                          : t("dashboard.charts.creditSpendTotal", {
                              displayCredits: formatCredits(
                                creditUsage?.totalCredits ?? 0
                              ),
                            })}
                    </p>
                  </div>
                  {canOpenCreditUsageDetails ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 px-2"
                      onClick={openCreditUsageDetails}
                    >
                      {t("dashboard.charts.creditSpendDetails")}
                    </Button>
                  ) : null}
                </div>
              </CardHeader>
              <div className="h-[180px] cursor-pointer px-4 pb-5">
                <CreditUsageTrendChartCard
                  data={creditUsageChartData}
                  isLoading={isCreditUsageLoading}
                  emptyLabel={
                    creditUsageErrorMessage
                      ? creditUsageErrorMessage
                      : hasCreditUsageData
                        ? t("dashboard.emptyState")
                        : t("dashboard.emptyReasons.noCreditUsage")
                  }
                  formatValue={formatCredits}
                  yAxisWidth={42}
                  canDrillIntoRecords={canOpenCreditUsageDetails}
                  partialDate={creditUsage?.rangeEnd}
                  onDateClick={
                    canOpenCreditUsageDetails
                      ? (date) => {
                          setSelectedCreditUsageDate(date)
                          setCreditUsageModalOpen(true)
                        }
                      : undefined
                  }
                  embedded
                />
              </div>
            </Card>

            {showUsageCharts ? (
              <ChartCard
                title={t("dashboard.charts.dailyUnique")}
                data={chartData.unique}
                color="#2563EB"
                isLoading={isLoading}
                emptyLabel={emptyUsageLabel}
                seriesLabel={t("dashboard.charts.seriesActiveUsers")}
                partialDate={dashboardData?.rangeEnd}
                chartHeight={180}
              />
            ) : null}
          </div>
        </section>
      )}

      {showPostLaunchSections && showFormCharts && (
        <div className="grid gap-4 lg:grid-cols-2">
          <MultiLineChartCard
            title={t("dashboard.charts.dailyFormToolCallsByAgent")}
            data={chartData.formCalls.data}
            series={chartData.formCalls.seriesMeta}
            isLoading={isLoading}
            emptyLabel={emptyFormsLabel}
            partialDate={dashboardData?.rangeEnd}
          />
          <MultiLineChartCard
            title={t("dashboard.charts.dailyFormSubmissionsByAgent")}
            data={chartData.formSubmissions.data}
            series={chartData.formSubmissions.seriesMeta}
            isLoading={isLoading}
            emptyLabel={emptyFormsLabel}
            partialDate={dashboardData?.rangeEnd}
          />
        </div>
      )}
    </div>
  )
}

export default DashboardPage
